/**
 * Workspace Slice
 * Manages the list of apps/tools available to the current authenticated user's
 * active tenant. Kept separate from availableAppsSlice (used by /main) to avoid
 * shared-state conflicts between the two pages.
 *
 * Requirements: 1.2, 1.4, 1.5
 */

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SysAppDto } from '../../services/api/sysAppService';
import { fetchWorkspaceApps } from '../thunks/workspaceThunks';

export interface WorkspaceState {
  apps: SysAppDto[];
  useCustomWorkspace: boolean;
  /** Tenant-scoped company name override; null means use global branding */
  tenantCompanyName: string | null;
  /** Tenant-resolved page logo URL; null means render the built-in default logo */
  tenantLogoUrl: string | null;
  /** Tenant-resolved header-corner icon URL; null means render the Default Tenant logo */
  tenantLogoIconUrl: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: WorkspaceState = {
  apps: [],
  useCustomWorkspace: false,
  tenantCompanyName: null,
  tenantLogoUrl: null,
  tenantLogoIconUrl: null,
  isLoading: false,
  error: null,
};

const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {
    clearWorkspaceApps: (state) => {
      state.apps = [];
      state.useCustomWorkspace = false;
      state.tenantCompanyName = null;
      state.tenantLogoUrl = null;
      state.tenantLogoIconUrl = null;
      state.error = null;
    },
    /**
     * Sets the tenant display name + logo the header renders. Used by the
     * tenant landing page (`/welcome/{tenantCode}`), where the workspace
     * bootstrap (`fetchWorkspaceApps`) does not run — so the header would
     * otherwise have no tenant branding to show (spec: tenant-landing-page).
     */
    setTenantBranding: (
      state,
      action: PayloadAction<{
        tenantCompanyName: string | null;
        tenantLogoUrl: string | null;
        tenantLogoIconUrl: string | null;
      }>,
    ) => {
      state.tenantCompanyName = action.payload.tenantCompanyName;
      state.tenantLogoUrl = action.payload.tenantLogoUrl;
      state.tenantLogoIconUrl = action.payload.tenantLogoIconUrl;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWorkspaceApps.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        // Intentionally does NOT clear tenantCompanyName / LogoUrl /
        // LogoIconUrl here. The cross-tenant case is covered by the
        // setActiveTenant matcher below (clears in the same reducer pass).
        // Clearing again on .pending would wipe a value the picker just
        // pre-resolved (see TenantPickerModal.handleSelect) and produce the
        // exact "legal name middle frame" we set the pre-resolve up to
        // avoid. Same-tenant refetch keeps the existing values until
        // .fulfilled writes them back identically — no visible change.
      })
      .addCase(fetchWorkspaceApps.fulfilled, (state, action) => {
        state.isLoading = false;
        state.apps = action.payload.apps;
        state.useCustomWorkspace = action.payload.useCustomWorkspace;
        state.tenantCompanyName = action.payload.tenantCompanyName ?? null;
        state.tenantLogoUrl = action.payload.tenantLogoUrl ?? null;
        state.tenantLogoIconUrl = action.payload.tenantLogoIconUrl ?? null;
      })
      .addCase(fetchWorkspaceApps.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Cross-slice: every time the active tenant changes (picker, TenantLoader
      // restore, workspace layout URL reconcile, etc.) the previous tenant's
      // resolved branding must be dropped in the SAME reducer pass — otherwise
      // the header reads `activeTenant=TLT` from tenantSlice but still sees
      // `tenantCompanyName="Zebra Technologies"` here, producing the
      // mismatched corner/body frame during switches (Zebra header next to
      // Tradelink body). The new tenant's `fetchWorkspaceApps` then
      // repopulates these in `.fulfilled`. Matching by literal action type
      // keeps this slice free of tenantSlice imports (no circular dependency
      // through middleware/listener wiring). NB: builder requires `addMatcher`
      // calls after all `addCase` calls.
      .addMatcher(
        (action: { type?: string }) => action.type === 'tenant/setActiveTenant',
        (state) => {
          state.tenantCompanyName = null;
          state.tenantLogoUrl = null;
          state.tenantLogoIconUrl = null;
        },
      );
  },
});

export const { clearWorkspaceApps, setTenantBranding } = workspaceSlice.actions;
export default workspaceSlice.reducer;
